import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AssignmentOutlined as AssignmentIcon,
  CheckCircleOutline as CheckCircleIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import empreendimentoService from "../services/empreendimento";
import isencaoLicenciamentoConsultaService from "../services/isencaoLicenciamentoConsulta";
import CharCounter from "./CharCounter";

const ATIVIDADE_MAX = 300;

const CAMPOS = [
  {
    key: "declaracao",
    label: "Declaração",
    descricao: "Apresentar declaração do requerente na qual deverá informar que o empreendimento se enquadra nas atividades.",
  },
  {
    key: "oficio",
    label: "Ofício",
    descricao: "Documento informando o motivo da solicitação de declaração. O interessado deverá descrever em detalhes a atividade para a qual está solicitando a isenção, informar também um par de coordenadas e descrever a área em m2 além de sua localização.",
  },
  {
    key: "documentacaoInteressado",
    label: "Documentação do Interessado",
    descricao: "Para pessoa física: Cadastro de Pessoa Física (CPF) e documento identificador com foto.",
  },
  {
    key: "comprovanteEndereco",
    label: "Comprovante de Endereço",
    descricao: "Expedido nos últimos 60 dias.",
  },
  {
    key: "declaracaoMeMei",
    label: "Declaração ME/MEI",
    descricao: "Comprovante que a empresa se trata de um ME/MEI.",
  },
];

function NovoRequerimentoIsencaoModal({ open, onClose, onSuccess }) {
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [empreendimentoSelecionado, setEmpreendimentoSelecionado] = useState(null);
  const [atividade, setAtividade] = useState("");
  const [arquivos, setArquivos] = useState({});
  const [errosArquivo, setErrosArquivo] = useState({});
  const [erroCampos, setErroCampos] = useState({});
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const inputRefs = useRef({});

  useEffect(() => {
    if (open) {
      setEmpreendimentoSelecionado(null);
      setAtividade("");
      setArquivos({});
      setErrosArquivo({});
      setErroCampos({});
      setErro("");
      empreendimentoService
        .listarPaginado({}, 1, 100)
        .then((result) => setEmpreendimentos(result.items || []))
        .catch(() => setEmpreendimentos([]));
    }
  }, [open]);

  const isFormatoPermitido = (file) => {
    const nome = file.name.toLowerCase();
    return (
      file.type === "application/pdf" ||
      file.type === "image/png" ||
      nome.endsWith(".pdf") ||
      nome.endsWith(".png")
    );
  };

  const handleArquivoChange = (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isFormatoPermitido(file)) {
      setErrosArquivo((prev) => ({ ...prev, [key]: "Apenas arquivos PDF ou PNG são aceitos." }));
      setArquivos((prev) => ({ ...prev, [key]: null }));
      e.target.value = "";
      return;
    }
    setErrosArquivo((prev) => ({ ...prev, [key]: "" }));
    setErroCampos((prev) => ({ ...prev, [key]: "" }));
    setArquivos((prev) => ({ ...prev, [key]: file }));
  };

  const handleRemoverArquivo = (key) => {
    setArquivos((prev) => ({ ...prev, [key]: null }));
    if (inputRefs.current[key]) inputRefs.current[key].value = "";
  };

  const validar = () => {
    const erros = {};
    if (!empreendimentoSelecionado) erros.empreendimento = "Selecione um empreendimento.";
    if (!atividade.trim()) erros.atividade = "Informe a atividade.";
    CAMPOS.forEach(({ key, label }) => {
      if (!arquivos[key]) erros[key] = `O arquivo "${label}" é obrigatório.`;
    });
    setErroCampos(erros);
    return Object.keys(erros).length === 0;
  };

  const handleEnviar = async () => {
    if (!validar()) return;
    setLoading(true);
    setErro("");
    try {
      const response = await isencaoLicenciamentoConsultaService.criar(
        empreendimentoSelecionado.id,
        atividade.trim(),
        arquivos.declaracao,
        arquivos.oficio,
        arquivos.documentacaoInteressado,
        arquivos.comprovanteEndereco,
        arquivos.declaracaoMeMei
      );
      const isencaoId = response?.data?.id ?? null;
      onSuccess(isencaoId);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        "Erro ao enviar requerimento. Tente novamente.";
      setErro(msg);
    } finally {
      setLoading(false);
    }
  };

  const uploadedCount = CAMPOS.filter(({ key }) => !!arquivos[key]).length;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AssignmentIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={600} lineHeight={1.3}>
              Novo Requerimento de Isenção
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Preencha os dados e anexe os documentos necessários
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {erro && (
          <Alert
            severity="error"
            sx={{ mb: 2.5, "& .MuiAlert-message": { whiteSpace: "pre-line" } }}
          >
            {erro}
          </Alert>
        )}

        <Autocomplete
          options={empreendimentos}
          getOptionLabel={(op) => op.nome || op.razaoSocial || ""}
          value={empreendimentoSelecionado}
          onChange={(_, val) => {
            setEmpreendimentoSelecionado(val);
            setErroCampos((prev) => ({ ...prev, empreendimento: "" }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Empreendimento *"
              error={!!erroCampos.empreendimento}
              helperText={erroCampos.empreendimento}
            />
          )}
          sx={{ mb: 3 }}
        />

        <TextField
          fullWidth
          label="Atividade *"
          value={atividade}
          onChange={(e) => {
            setAtividade(e.target.value);
            if (erroCampos.atividade) setErroCampos((prev) => ({ ...prev, atividade: "" }));
          }}
          error={!!erroCampos.atividade}
          helperText={erroCampos.atividade || "Descreva a atividade objeto desta solicitação de isenção."}
          inputProps={{ maxLength: ATIVIDADE_MAX }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <CharCounter value={atividade} maxLength={ATIVIDADE_MAX} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={700}
            lineHeight={1}
          >
            Documentos Necessários
          </Typography>
          <Chip
            label={`${uploadedCount} / ${CAMPOS.length}`}
            size="small"
            color={uploadedCount === CAMPOS.length ? "success" : "default"}
            variant="outlined"
          />
        </Box>

        <Stack spacing={1.5}>
          {CAMPOS.map(({ key, label, descricao }) => {
            const arquivo = arquivos[key];
            const erroArq = errosArquivo[key];
            const erroCampo = erroCampos[key];
            const hasError = !!(erroArq || erroCampo);

            return (
              <Box key={key}>
                <Box
                  sx={{
                    border: "1.5px solid",
                    borderColor: hasError
                      ? "error.main"
                      : arquivo
                      ? "success.main"
                      : "divider",
                    borderRadius: 2,
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    transition: "border-color 0.2s",
                    "&:hover": !arquivo
                      ? { borderColor: "primary.main" }
                      : {},
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "action.selected",
                      flexShrink: 0,
                    }}
                  >
                    {arquivo ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <UploadFileIcon color="action" fontSize="small" />
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {label}
                      <Typography component="span" color="error.main">
                        {" "}*
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                      {descricao}
                    </Typography>
                    {arquivo ? (
                      <Typography
                        variant="caption"
                        color="success.dark"
                        noWrap
                        display="block"
                      >
                        {arquivo.name}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        Nenhum arquivo selecionado
                      </Typography>
                    )}
                  </Box>

                  {arquivo ? (
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => handleRemoverArquivo(key)}
                      sx={{ color: "text.secondary", minWidth: "auto", flexShrink: 0 }}
                    >
                      Remover
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      component="label"
                      sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                    >
                      Selecionar
                      <input
                        type="file"
                        accept=".pdf,application/pdf,.png,image/png"
                        hidden
                        ref={(el) => (inputRefs.current[key] = el)}
                        onChange={(e) => handleArquivoChange(key, e)}
                      />
                    </Button>
                  )}
                </Box>

                {(erroArq || erroCampo) && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ ml: 1, mt: 0.25, display: "block" }}
                  >
                    {erroArq || erroCampo}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleEnviar}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? "Enviando..." : "Enviar Requerimento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NovoRequerimentoIsencaoModal;
